import React, { useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Merge, Search, RefreshCw, ShieldAlert, Cloud } from "lucide-react";

export default function KairosSync() {
  const [engFile, setEngFile] = useState(null);
  const [engUploading, setEngUploading] = useState(false);
  const [engResult, setEngResult] = useState(null);
  const [engDownloading, setEngDownloading] = useState(false);
  const [engPreview, setEngPreview] = useState(null);
  const [engReplace, setEngReplace] = useState(false);
  const [apiSyncBusy, setApiSyncBusy] = useState(false);
  const [apiSyncResult, setApiSyncResult] = useState(null);
  const [apiSyncStatus, setApiSyncStatus] = useState(null);
  const [apiWipeConfirm, setApiWipeConfirm] = useState(false);

  React.useEffect(() => {
    api.get("/kairos/sync-status")
      .then(r => setApiSyncStatus(r.data))
      .catch(() => setApiSyncStatus(null));
  }, []);

  const syncFromApi = async (opts = {}) => {
    const { wipe = false, dryRun = false } = opts;
    setApiSyncBusy(true);
    if (!dryRun) setApiSyncResult(null);
    try {
      const params = new URLSearchParams();
      if (wipe) params.set("wipe", "true");
      if (dryRun) params.set("dry_run", "true");
      const q = params.toString();
      const url = q ? `/kairos/sync-from-api?${q}` : "/kairos/sync-from-api";
      const res = await api.post(url, null, { headers: { "Content-Type": "application/json" } });
      setApiSyncResult(res.data);
      setApiWipeConfirm(false);
      if (!dryRun) {
        const s = await api.get("/kairos/sync-status");
        setApiSyncStatus(s.data);
      }
      const bits = [
        dryRun ? "Dry-run preview ready" :
          (wipe ? `Wiped ${res.data.wiped?.language_projects || 0} projects` : ""),
        `${res.data.updated + res.data.inserted} rows synced`,
        res.data.pruned ? `${res.data.pruned} pruned` : "",
        res.data.registry_projects_created ? `${res.data.registry_projects_created} registry added` : "",
      ].filter(Boolean).join(" · ");
      toast.success(bits || "Sync complete");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Geneza sync failed — DB untouched");
    } finally { setApiSyncBusy(false); }
  };

  const uploadEngagements = async (dryRun = false) => {
    if (!engFile) return toast.error("Choose the Language Engagement file first");
    setEngUploading(true);
    if (!dryRun) setEngResult(null);
    try {
      const fd = new FormData();
      fd.append("file", engFile);
      const params = new URLSearchParams();
      if (dryRun) params.set("dry_run", "true");
      if (!dryRun && engReplace) params.set("replace", "true");
      const q = params.toString();
      const url = q ? `/languages/import?${q}` : "/languages/import";
      const res = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (dryRun) {
        setEngPreview(res.data);
        toast.success(`Preview ready — ${res.data.mergers_count} merger${res.data.mergers_count === 1 ? "" : "s"} detected`);
      } else {
        setEngResult(res.data);
        setEngPreview(null);
        const bits = [
          `${res.data.updated + res.data.inserted} synced`,
          res.data.relinked ? `${res.data.relinked} legacy keys relinked` : "",
          res.data.wiped ? `${res.data.wiped} wiped first` : "",
        ].filter(Boolean).join(" · ");
        toast.success(bits);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setEngUploading(false); }
  };

  const downloadEngagements = async () => {
    setEngDownloading(true);
    try {
      const token = localStorage.getItem("bt_token");
      const res = await fetch(`${API_BASE}/languages/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `Language_Engagements_${Date.now()}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Language engagements downloaded");
    } catch (err) {
      toast.error(err.message || "Download failed");
    } finally { setEngDownloading(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-10 py-10 fade-in-up" data-testid="kairos-sync-page">
      <div className="mb-8">
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-2">
          Admin · Data Sync
        </div>
        <h1 className="serif text-5xl tracking-tight leading-none">Project Management Dashboard</h1>
        <p className="text-sm text-[color:var(--ink-2)] mt-3 max-w-2xl">
          Sync the KAIROS Project Progress data live from Geneza's Matthew Reports API,
          or export and re-import the BTC Language Engagement database as a workbook.
          Notes, issues, hubs and clusters are preserved on every language engagement.
        </p>
      </div>

      {/* Geneza API sync — the primary, recommended flow */}
      <div className="hairline bg-white p-8 mb-8" style={{ borderColor: "var(--accent)", borderWidth: 2 }} data-testid="geneza-sync-panel">
        <div className="flex items-start gap-3">
          <Cloud size={28} strokeWidth={1.5} className="text-[color:var(--accent)] shrink-0 mt-1" />
          <div className="flex-1">
            <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--accent)] mb-2">
              Recommended · Live from Source
            </div>
            <div className="serif text-2xl tracking-tight mb-2">Sync from Geneza API</div>
            <p className="text-sm text-[color:var(--ink-2)] mb-4 max-w-2xl">
              Pull the latest KAIROS workbook directly from Geneza's Matthew Reports API
              (<span className="mono text-xs">/api/dashboard/matthew · database=AG Prod · live=true</span>) and
              neat-sync into the database. No browser upload, no Cloudflare timeout risk,
              nightly job also runs automatically at 02:00 UTC. On any Geneza-side error
              the DB is left untouched.
            </p>
            {apiSyncStatus?.at && (
              <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-3)] mb-4">
                Last successful sync: {new Date(apiSyncStatus.at).toLocaleString()} · by {apiSyncStatus.by || "—"}
                {" · "}{apiSyncStatus.updated || 0} rows updated
                {(apiSyncStatus.registry_projects_created || 0) > 0 && ` · ${apiSyncStatus.registry_projects_created} registry added`}
              </div>
            )}
            <div className="grid grid-cols-3 gap-3 max-w-2xl">
              <button
                className="btn-ghost text-sm flex items-center gap-2 justify-center"
                onClick={() => syncFromApi({ dryRun: true })}
                disabled={apiSyncBusy}
                data-testid="geneza-preview-btn"
                title="Fetch and analyse without writing to the DB."
              >
                <RefreshCw size={14} /> {apiSyncBusy && !apiSyncResult ? "Fetching…" : "Preview"}
              </button>
              <button
                className="btn-primary text-sm flex items-center gap-2 justify-center"
                onClick={() => syncFromApi({ wipe: false })}
                disabled={apiSyncBusy}
                data-testid="geneza-sync-btn"
              >
                <Cloud size={14} /> {apiSyncBusy ? "Syncing…" : "Sync now"}
              </button>
              <button
                className="btn-primary text-sm flex items-center gap-2 justify-center"
                style={{ background: "var(--behind)" }}
                onClick={() => setApiWipeConfirm(true)}
                disabled={apiSyncBusy}
                data-testid="geneza-wipe-btn"
                title="Wipe every project + book-stage, then rebuild from Geneza. Destructive."
              >
                <ShieldAlert size={14} /> Wipe & Sync
              </button>
            </div>
            {apiWipeConfirm && (
              <div className="mt-4 p-4 border border-[color:var(--behind)]" data-testid="geneza-wipe-confirm">
                <div className="serif text-lg mb-1">Confirm destructive wipe</div>
                <p className="text-sm text-[color:var(--ink-2)] mb-3">
                  This will <strong>delete every language project and every book stage</strong>{" "}
                  in the database, then rebuild live from Geneza. Manual notes and issues on
                  the Language Engagement DB are untouched. Irreversible.
                </p>
                <div className="flex gap-2">
                  <button className="btn-ghost text-sm flex-1" onClick={() => setApiWipeConfirm(false)} disabled={apiSyncBusy}>
                    Cancel
                  </button>
                  <button
                    className="btn-primary text-sm flex-1 flex items-center gap-2 justify-center"
                    style={{ background: "var(--behind)" }}
                    onClick={() => syncFromApi({ wipe: true })}
                    disabled={apiSyncBusy}
                    data-testid="geneza-wipe-confirm-btn"
                  >
                    <ShieldAlert size={14} /> {apiSyncBusy ? "Wiping & Syncing…" : "Yes, wipe and sync"}
                  </button>
                </div>
              </div>
            )}
            {apiSyncResult && (
              <div className="mt-4 border-t border-[color:var(--border)] pt-4" data-testid="geneza-sync-result">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 size={16} className="text-[color:var(--on-track)]" />
                  <div className="serif text-lg">
                    {apiSyncResult.dry_run ? "Preview" : "Sync complete"}
                    {" · "}{apiSyncResult.source?.filename}
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <ResultCell label="Rows synced" v={(apiSyncResult.updated || 0) + (apiSyncResult.inserted || 0)} tone="ontrack" />
                  <ResultCell label="Registry adds" v={apiSyncResult.registry_projects_created || 0} tone={apiSyncResult.registry_projects_created ? "ontrack" : ""} />
                  <ResultCell label={apiSyncResult.dry_run ? "Would prune" : "Pruned"} v={apiSyncResult.dry_run ? apiSyncResult.prune_candidates_count : apiSyncResult.pruned} tone={(apiSyncResult.pruned || apiSyncResult.prune_candidates_count) ? "amber" : ""} />
                  <ResultCell label="Duplicates merged" v={apiSyncResult.duplicates_merged || 0} tone={apiSyncResult.duplicates_merged ? "amber" : ""} />
                </div>
                <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-3)] mt-3">
                  Source: {apiSyncResult.source?.database} · {(apiSyncResult.source?.bytes / 1024 / 1024).toFixed(2)} MB
                  {apiSyncResult.wipe_mode && ` · wiped ${apiSyncResult.wiped?.language_projects || 0} projects first`}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>


      {/* Language Engagement Sync — Download + Upload */}
      <div className="mt-12">
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-2">
          BTC Language Engagement Data
        </div>
        <h2 className="serif text-5xl tracking-tight leading-none mb-3">Language Engagement Sync</h2>
        <p className="text-sm text-[color:var(--ink-2)] mb-6 max-w-2xl">
          Download the current engagement database as a fresh Excel workbook, edit offline, then
          upload it back. Rows are aggregated by unique
          <span className="mono text-xs mx-1">Language + ISO + RoLV + Country</span>.
          Language names are normalised for comparison (e.g. "Nayak (Banjara)" merges with
          "Nayak Banjara"); the preview screen lists every such merger before write. Existing
          <span className="mono text-xs mx-1">notes · issues · hub · cluster</span>
          and admin-defined scope are preserved on every row.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Download */}
          <div className="hairline bg-white p-8" data-testid="eng-download-panel">
            <FileSpreadsheet size={28} strokeWidth={1.5} className="text-[color:var(--accent)] mb-4" />
            <div className="serif text-2xl tracking-tight mb-2">Download</div>
            <p className="text-sm text-[color:var(--ink-2)] mb-6">
              Exports every language engagement into a fresh <span className="mono text-xs">.xlsx</span>{" "}
              workbook — one row per unique ISO × RoLV × Country combination with
              organizations, involvement, activities, translation goals, statuses,
              current scope, completed products and note / issue counts.
            </p>
            <button
              className="btn-primary flex items-center gap-2 text-sm w-full justify-center"
              onClick={downloadEngagements}
              disabled={engDownloading}
              data-testid="eng-download-btn"
            >
              <Download size={14} />{" "}
              {engDownloading ? "Preparing…" : "Download Language-Engagements.xlsx"}
            </button>
          </div>

          {/* Upload */}
          <div className="hairline bg-white p-8" data-testid="eng-upload-panel">
            <Upload size={28} strokeWidth={1.5} className="text-[color:var(--behind)] mb-4" />
            <div className="serif text-2xl tracking-tight mb-2">Upload</div>
            <p className="text-sm text-[color:var(--ink-2)] mb-6">
              Upload the BTC master language engagement workbook. Matched by
              ISO + RoLV + Country; source-derived fields are updated while notes,
              issues, hub and cluster are preserved untouched.
            </p>
            <label className="block mb-4">
              <input
                type="file"
                accept=".xlsx,.xlsm"
                onChange={(e) => { setEngFile(e.target.files?.[0] || null); setEngResult(null); }}
                className="hidden"
                id="eng-upload-file"
                data-testid="eng-file-input"
              />
              <div className="border-2 border-dashed border-[color:var(--border-strong)] hover:border-[color:var(--ink)] p-6 text-center cursor-pointer transition-colors">
                <div className="mono text-xs text-[color:var(--ink-2)]">
                  {engFile ? engFile.name : "Click to choose Language Engagement .xlsx"}
                </div>
                {engFile && (
                  <div className="mono text-[10px] text-[color:var(--ink-3)] mt-1">
                    {(engFile.size / 1024).toFixed(1)} KB
                  </div>
                )}
              </div>
            </label>
            <div className="flex gap-2">
              <label htmlFor="eng-upload-file" className="btn-ghost text-sm flex-1 text-center cursor-pointer">
                {engFile ? "Change file" : "Choose file"}
              </label>
              <button
                className="btn-primary text-sm flex-1 flex items-center gap-2 justify-center"
                onClick={() => uploadEngagements(true)}
                disabled={!engFile || engUploading}
                data-testid="eng-preview-btn"
              >
                <Upload size={14} /> {engUploading && !engPreview ? "Analysing…" : "Preview merges"}
              </button>
            </div>
          </div>
        </div>

        {/* Merger preview card — shown before actual write */}
        {engPreview && (
          <div className="hairline bg-white mt-6 p-6" style={{ borderColor: "var(--amber)" }} data-testid="eng-merge-preview">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle size={18} style={{ color: "var(--amber)" }} />
              <div className="serif text-xl">Review before importing</div>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-5">
              <ResultCell label="Source rows" v={engPreview.total_rows} />
              <ResultCell label="Unique keys" v={engPreview.unique_keys} />
              <ResultCell label="Name mergers" v={engPreview.mergers_count} tone={engPreview.mergers_count ? "amber" : ""} />
            </div>
            {engPreview.mergers_count > 0 ? (
              <>
                <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-2">
                  These raw language names will collapse into one engagement each
                </div>
                <div className="max-h-64 overflow-y-auto border border-[color:var(--border)]">
                  <table className="w-full text-xs">
                    <thead className="bg-[color:var(--surface-2)] sticky top-0">
                      <tr className="text-left">
                        <th className="px-3 py-2 mono uppercase tracking-widest text-[10px]">ISO</th>
                        <th className="px-3 py-2 mono uppercase tracking-widest text-[10px]">Country</th>
                        <th className="px-3 py-2 mono uppercase tracking-widest text-[10px]">RoLV</th>
                        <th className="px-3 py-2 mono uppercase tracking-widest text-[10px]">Raw variants → merged into one</th>
                        <th className="px-3 py-2 mono uppercase tracking-widest text-[10px] text-right">Rows</th>
                      </tr>
                    </thead>
                    <tbody>
                      {engPreview.mergers.map((m, i) => (
                        <tr key={i} className="border-t border-[color:var(--border)]">
                          <td className="px-3 py-2 mono">{m.iso}</td>
                          <td className="px-3 py-2">{m.country || "—"}</td>
                          <td className="px-3 py-2 mono">{m.rolv || "—"}</td>
                          <td className="px-3 py-2">{m.variants.join("  ·  ")}</td>
                          <td className="px-3 py-2 mono text-right">{m.source_rows}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="text-sm text-[color:var(--ink-2)] italic">
                No punctuation-based mergers detected. Import will apply cleanly.
              </div>
            )}
            <div className="flex gap-2 mt-6 items-start">
              <label className="flex items-start gap-2 flex-1 text-xs text-[color:var(--ink-2)] cursor-pointer" title="Wipe every engagement first — use to fix schema drift">
                <input
                  type="checkbox"
                  checked={engReplace}
                  onChange={(e) => setEngReplace(e.target.checked)}
                  data-testid="eng-replace-toggle"
                  className="mt-0.5"
                />
                <span>
                  <strong>Replace all</strong> — clean slate. Wipes every existing engagement
                  first (and its notes/issues/hub/cluster/scope). Use if row counts keep growing
                  across re-imports.
                </span>
              </label>
            </div>
            <div className="flex gap-2 mt-3">
              <button
                className="btn-ghost text-sm flex-1"
                onClick={() => { setEngPreview(null); setEngReplace(false); }}
                data-testid="eng-cancel-btn"
              >
                Cancel
              </button>
              <button
                className="btn-primary text-sm flex-1 flex items-center gap-2 justify-center"
                onClick={() => uploadEngagements(false)}
                disabled={engUploading}
                data-testid="eng-confirm-btn"
              >
                <CheckCircle2 size={14} /> {engUploading ? "Importing…" : (engReplace ? "Wipe & Import" : "Confirm & Import")}
              </button>
            </div>
          </div>
        )}

        {engResult && (
          <div className="hairline bg-white mt-6 p-6" data-testid="eng-upload-result">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 size={18} className="text-[color:var(--on-track)]" />
              <div className="serif text-xl">Language engagement sync complete</div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <ResultCell label="Rows read" v={engResult.total_rows} />
              <ResultCell label="Unique keys" v={engResult.unique_keys ?? engResult.unique_combos} />
              <ResultCell label="Updated" v={(engResult.updated || 0) + (engResult.relinked || 0)} tone="ontrack" />
              <ResultCell label="Inserted" v={engResult.inserted} tone="ontrack" />
            </div>
            {(engResult.relinked > 0 || engResult.wiped > 0) && (
              <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-3)] mt-3">
                {engResult.wiped > 0 && <>Wiped {engResult.wiped} pre-existing records · </>}
                {engResult.relinked > 0 && <>{engResult.relinked} legacy-key records auto-migrated to new format</>}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-8 mono text-[10px] uppercase tracking-widest text-[color:var(--ink-3)] text-center">
        Only administrators can access this module
      </div>

      <ManualMergeTool />
    </div>
  );
}

function ResultCell({ label, v, tone }) {
  const color = tone === "ontrack" ? "text-[color:var(--on-track)]"
    : tone === "amber" ? "text-[color:var(--amber)]"
    : "";
  return (
    <div className="border border-[color:var(--border)] p-4">
      <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-1">{label}</div>
      <div className={`serif text-3xl tracking-tight ${color}`}>{v}</div>
    </div>
  );
}


function ManualMergeTool() {
  const [src, setSrc] = useState(null);
  const [tgt, setTgt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const doMerge = async () => {
    if (!src || !tgt) return toast.error("Pick both a source and a target engagement");
    if (src.key === tgt.key) return toast.error("Source and target must differ");
    setBusy(true);
    try {
      const res = await api.post("/languages/engagements/merge", {
        source_key: src.key, target_key: tgt.key,
      });
      setResult(res.data);
      toast.success(`Merged — ${res.data.notes_transferred} notes and ${res.data.issues_transferred} issues moved`);
      setSrc(null); setTgt(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Merge failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-14" data-testid="manual-merge-tool">
      <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-2">
        Admin · Data Reconciliation
      </div>
      <h2 className="serif text-5xl tracking-tight leading-none mb-3">Manual Merge</h2>
      <p className="text-sm text-[color:var(--ink-2)] mb-6 max-w-2xl">
        Field teams sometimes flag two engagements (different ISO / RoLV / spelling) as the
        same language. Pick a <strong>source</strong> and a <strong>target</strong> — the
        source's notes, issues, organisations and statuses fold into the target, the source
        record is deleted, and both the target's <span className="mono text-xs">hub · cluster</span>
        and admin scope are kept.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <EngagementPicker label="Source (will be merged & deleted)" selected={src} onPick={setSrc} testid="merge-src" />
        <EngagementPicker label="Target (keeps its key, absorbs source)" selected={tgt} onPick={setTgt} testid="merge-tgt" />
      </div>

      {src && tgt && (
        <div className="hairline bg-white mt-6 p-6" data-testid="merge-preview">
          <div className="flex items-center gap-2 mb-4">
            <Merge size={18} className="text-[color:var(--accent)]" />
            <div className="serif text-xl">Merge preview</div>
          </div>
          <div className="grid grid-cols-2 gap-6 items-center">
            <EngagementSummary e={src} tone="behind" tag="Source" />
            <EngagementSummary e={tgt} tone="ontrack" tag="Target" />
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <button className="btn-ghost text-sm" onClick={() => { setSrc(null); setTgt(null); }}>
              Cancel
            </button>
            <button
              className="btn-primary text-sm flex items-center gap-2"
              onClick={doMerge}
              disabled={busy}
              data-testid="merge-confirm"
            >
              <Merge size={14} /> {busy ? "Merging…" : "Merge & Delete Source"}
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="hairline bg-white mt-6 p-6 flex items-center gap-3" data-testid="merge-result">
          <CheckCircle2 size={20} className="text-[color:var(--on-track)]" />
          <div>
            <div className="serif text-xl">Merge complete</div>
            <div className="text-sm text-[color:var(--ink-2)]">
              {result.notes_transferred} notes and {result.issues_transferred} issues moved
              to <span className="mono text-xs">{result.target_key}</span>. Recorded in the audit log.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function EngagementPicker({ label, selected, onPick, testid }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  React.useEffect(() => {
    const t = setTimeout(async () => {
      if (!q.trim()) return setItems([]);
      setLoading(true);
      try {
        const res = await api.get(`/languages/engagements-lookup?q=${encodeURIComponent(q)}`);
        setItems(res.data);
      } catch { setItems([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="hairline bg-white p-5" data-testid={testid}>
      <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-3">
        {label}
      </div>
      {selected ? (
        <div>
          <EngagementSummary e={selected} />
          <button className="btn-ghost text-xs mt-3" onClick={() => onPick(null)}>
            Change
          </button>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 border border-[color:var(--border)] px-2">
            <Search size={14} className="text-[color:var(--ink-3)]" />
            <input
              className="w-full text-sm py-2 outline-none bg-transparent"
              placeholder="Search by ISO, language, country, RoLV…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid={`${testid}-input`}
            />
          </div>
          <div className="max-h-64 overflow-y-auto mt-2">
            {loading && <div className="mono text-[10px] text-[color:var(--ink-3)] p-2">Searching…</div>}
            {!loading && items.length === 0 && q && (
              <div className="mono text-[10px] text-[color:var(--ink-3)] p-2">No matches</div>
            )}
            {items.map((it) => (
              <button
                key={it.key}
                onClick={() => onPick(it)}
                className="w-full text-left px-3 py-2 border-t border-[color:var(--border)] hover:bg-[color:var(--surface-2)] text-sm"
                data-testid={`${testid}-row`}
              >
                <div className="flex items-center gap-3">
                  <span className="mono text-[10px] text-[color:var(--ink-3)] w-14">{it.iso}</span>
                  <span className="flex-1">{it.language_name || "—"}</span>
                  <span className="text-xs text-[color:var(--ink-2)]">{it.country}</span>
                  {it.rolv && <span className="mono text-[10px] text-[color:var(--ink-3)]">{it.rolv}</span>}
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


function EngagementSummary({ e, tone, tag }) {
  const border = tone === "ontrack" ? "var(--on-track)"
    : tone === "behind" ? "var(--behind)"
    : "var(--border)";
  return (
    <div className="border-l-2 pl-3 py-1" style={{ borderColor: border }}>
      {tag && (
        <div className="mono text-[10px] uppercase tracking-widest mb-1" style={{ color: border }}>{tag}</div>
      )}
      <div className="serif text-xl tracking-tight">{e.language_name || "—"}</div>
      <div className="mono text-[10px] text-[color:var(--ink-3)]">
        ISO {e.iso} · {e.country}{e.rolv ? ` · RoLV ${e.rolv}` : ""}
      </div>
      <div className="text-xs text-[color:var(--ink-2)] mt-1">
        Statuses: {(e.statuses || []).join(", ") || "—"}
      </div>
      <div className="mono text-[10px] text-[color:var(--ink-3)] mt-1">
        {e.notes_count || 0} notes · {e.issues_count || 0} issues
      </div>
    </div>
  );
}
